/* istanbul ignore file */
// FIXME: Add tests

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DesignLayoutType,
  EventDesignPurpose,
  EventPageFragment,
  EventUserStatus,
  EventWebsiteGuestQuery,
  useEventWebsiteGuestQuery,
  useEventWebsiteGuestTelemetryQuery,
} from '@graphql/generated';
import { useImmer } from 'use-immer';
import { isInIframe } from '@shared/utils/isInIframe';
import { useEventUserRole } from '@shared/components/AuthProvider';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { shouldUseEventDesignDraft, useEventDesignTheming, useReloadForLegacyGuestsite, useGenerateGuestSiteLoadTelemData } from './GuestSite.utils';
import { useEventPageRouting } from './GuestSite.routes';
import { OnPreviewListenerUpdate } from './PreviewListener';
import { useGuestSiteTelemetry, enrichTelemetryExtraInfo } from '../../GuestSite.telemetry';
import type { StaticContext } from '@react-router';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useAppPage } from '@shared/utils/hooks/useAppPage';
import { useApolloClient } from '@apollo/client';
import { useGuestSiteHealth } from './GuestSite.health';
import { useSegmentGroupCallForEvent } from '@shared/core';
import { useComponentMountAndGraphqlQueryStatus } from '@shared/utils/hooks/useComponentMountAndGraphqlQueryStatus';
import { withWindow } from '@shared/utils/withWindow';
import { usePagesWithPhotoAlignment } from '@shared/customPage/hooks/usePagesWithPhotoAlignment';
import { useLayoutAndThemeOverride } from '@apps/common/hooks/useLayoutAndThemeOverride';
import { useEventPlaceCacheUpdate } from '@shared/utils/eventPlace';
import { useShouldHideRoomBlockPreview } from './hooks/useShouldHideRoomBlockPreview';
import { ViewMode } from './GuestSite.types';
import { useCurrentPageSlug } from '@shared/utils/hooks/useCurrentPageSlug';
import { useAccommodationPageChecks } from '@apps/guest/hooks/useAccommodationPageChecks';
import { useGetGuestSitePages } from './hooks/useGetGuestSitePages';
import { useWebsitePublishCapabilityFeature } from '@apps/admin/routes/WebsiteDesignerV2/hooks/useWebsitePublishCapabilityFeature';
import { useToast } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { addError } from '@shared/utils/logger';
import { EventFeature, useProFeature } from '@shared/hooks/useProFeature';
import { SupportedHeaderPhotoLayoutSchema } from '@apps/guest/websiteHeader';
import { pick } from 'lodash-es';
import { useAccommodationsVisibilityBanner } from './hooks/useAccommodationsVisibilityBanner';

interface GuestSiteArgs
  extends Readonly<{
    eventHandle: string;
    staticContext: (StaticContext & { guestSiteEventHandleToVerify?: string }) | undefined;
  }> {}

const isPreviewing = isInIframe();

//Pages contained own additional lists of data.
const refetchPageQueries: Record<string, string[]> = {
  story: ['GetGuestsiteCoupleStoryAndQandas'],
  schedule: ['GetGuestSiteSchedule'],
  travel: ['GetGuestsiteTravelList'],
  faq: ['GetGuestsiteFaq'],
  vip: ['GetGuestsiteVips'],
  registry: ['GetGuestsiteRegistry'],
  accommodations: ['EventHotelReservedRoomBlocks', 'GuestEventHotelRoomBlocks', 'GuestPageContainerBySlug'],
  custom: ['GuestPageContainerBySlug', 'GeneralScheduleComponentScheduleForEvent', 'PlaceToStayAccommodations'],
};

export type GuestSitePage = keyof typeof refetchPageQueries;

export const useGuestSiteController = (args: GuestSiteArgs) => {
  const client = useApolloClient();
  const { onLoadComplete, onLoadFailure } = useGuestSiteHealth();
  const { isAdmin, hasIdentifiedUserOnce, role, isSuperAdmin, eventUserStatus } = useEventUserRole();
  const { eventHandle, staticContext } = args;
  const [loadingTheme, setLoadingTheme] = useState(true);
  const [isLastPageVisible, setIsLastPageVisible] = useState(false);
  const addAppPage = useAppPage();
  const [hasEnrichedTelemetry, setHasEnrichedTelemetry] = useState(false);
  const { hideAccomPreviewUntilFlagEnabled } = useShouldHideRoomBlockPreview();
  const { toast } = useToast();
  const { tq } = useTranslation('publishReminder');

  const queryParamHelper = useQueryParamHelper();
  const { currentPageSlug } = useCurrentPageSlug(eventHandle);
  const { isNativeAccommodationPage } = useAccommodationPageChecks(currentPageSlug);

  const view = queryParamHelper.getValueString('view') as ViewMode;
  const isAnnouncementHidden = queryParamHelper.getValueString('hideannouncement') === 'true';

  // FeatureFlag => wait for StyleApplicator https://github.com/joylifeinc/joy-web/pull/3214
  const styledApplicatorLoading = useFeatureValue('enableLoadingStyleApplicator');
  const isHideGetTheAppPageExperimentEnabled = useFeatureValue('hideGetTheAppPageExperiment').value === 'treatment';

  const { executeSegmentGroupCallForEvent } = useSegmentGroupCallForEvent();

  //To define the previewed page on Admin
  const [currentAdminPage, setCurrentAdminPage] = useState('');

  const { loadingThemeData, getEventWithThemeAndLayout, hasOverrides, loadingFontPackData } = useLayoutAndThemeOverride({ ssr: true });

  const websitePublishCapabilityFeature = useWebsitePublishCapabilityFeature();
  const isLoadingWebsitePublishCapability = websitePublishCapabilityFeature.eventExposure.isLoading || websitePublishCapabilityFeature.eventPublishedState.isLoading;
  const isWebsitePublishCapabilityEnabled = websitePublishCapabilityFeature.eventExposure.value;

  const shouldUseDraft = !isWebsitePublishCapabilityEnabled && shouldUseEventDesignDraft();
  const eventDesignPurpose = shouldUseDraft ? EventDesignPurpose.draft : EventDesignPurpose.live;

  const { data, loading, error, refetch } = useEventWebsiteGuestQuery({
    variables: {
      eventHandle,
      eventDesignPurpose,
      isAdminDashboard: isPreviewing && isAdmin,
    },
    batchMode: 'fast',
    fetchPolicy: 'cache-first',
    // Guest site body is now rendered client-side only, so we need to wait until user auth is settled before fetching data.
    // This will be improved after we fix the AuthProvider initialization.
    skip: !hasIdentifiedUserOnce,
  });

  const { data: guestSiteLoadTelemData, loading: guestSiteLoadTelemDataLoading } = useEventWebsiteGuestTelemetryQuery({
    variables: {
      eventHandle,
      eventDesignPurpose,
      isAdminDashboard: isPreviewing && isAdmin,
    },
    fetchPolicy: 'cache-first',
    batchMode: 'fast',
    // Guest site body is now rendered client-side only, so we need to wait until user auth is settled before fetching data.
    // This will be improved after we fix the AuthProvider initialization.
    skip: !hasIdentifiedUserOnce || isLoadingWebsitePublishCapability,
  });

  useComponentMountAndGraphqlQueryStatus({ loading, error, onComplete: () => onLoadComplete(), onError: error => onLoadFailure('Failed to load GuestSite', error) });

  executeSegmentGroupCallForEvent(data?.eventByName?.firebaseId, {
    postgresEventId: data?.eventByName?.id,
    eventType: data?.eventByName?.info.eventType,
  });

  const [{ eventPreview }, setEventPreviewData] = useImmer<{ eventPreview: EventWebsiteGuestQuery | undefined }>(() => ({
    eventPreview: data,
  }));

  const [showBannerReminderChanges, setShowBannerReminderChanges] = useState(false);

  const [showAnnouncement, setShowAnnouncement] = useState(false);

  const isWebsitePublished = websitePublishCapabilityFeature.eventPublishedState.value;
  const isWebsitePublishedUpdating = websitePublishCapabilityFeature.eventPublishedState.isUpdating;
  const showUnpublishedBanner = hasIdentifiedUserOnce && !isPreviewing && isAdmin && isWebsitePublishCapabilityEnabled && !isWebsitePublished;
  const showUnpublishedPage = hasIdentifiedUserOnce && !isPreviewing && !isAdmin && isWebsitePublishCapabilityEnabled && !isWebsitePublished;

  const { showAccommodationsVisibilityBanner, handleCloseAccommodationsVisibilityBanner } = useAccommodationsVisibilityBanner({
    data,
    hasIdentifiedUserOnce,
    isAdmin,
    isPreviewing,
    isWebsitePublishCapabilityEnabled,
    isWebsitePublished,
  });

  const eventResponse = useMemo(() => {
    if (!data) return undefined;
    return hasOverrides ? getEventWithThemeAndLayout(data) : data;
  }, [data, hasOverrides, getEventWithThemeAndLayout]);

  // If previewing, use the clone. Else, use the original.
  const event = useMemo(() => {
    const whitelistedFields = ['featureFlags'];

    if (isPreviewing) {
      return eventPreview?.eventByName
        ? {
            ...eventPreview.eventByName,
            ...pick(eventPreview.eventByName || {}, whitelistedFields),
          }
        : undefined;
    }

    return eventResponse?.eventByName;
  }, [eventPreview?.eventByName, eventResponse?.eventByName]);

  const layout = isNativeAccommodationPage ? DesignLayoutType.brannan : event?.eventDesign?.websiteLayout.layoutType || DesignLayoutType.aloha;
  const hasUnpublishedChanges = event?.eventDesign?.hasUnpublishedChanges;

  if (staticContext) {
    staticContext.guestSiteEventHandleToVerify = eventHandle;
  }

  const { pages = [] } = event || {};

  const eventPageRoutes = useEventPageRouting(eventHandle, pages);

  // Update JoyKit theme object with event design preferences
  const { theme, loadingFonts } = useEventDesignTheming({ eventDesign: event?.eventDesign });

  const telemetry = useGuestSiteTelemetry();
  const updateContext = telemetry.updateContext;

  useEffect(() => {
    if (event && layout) updateContext(prev => ({ ...prev, eventId: event.id, layout }));
  }, [event, layout, updateContext]);

  const extraInfo = useGenerateGuestSiteLoadTelemData(guestSiteLoadTelemData?.eventByName, role);

  // Enrich all page view events with GuestSiteLoad properties to eliminate redundant GuestSiteLoad track event
  // No race condition: guestSiteLoadTelemDataLoading is included in the loading state,
  // so layouts won't render and page tracking won't start until enrichment completes
  useEffect(() => {
    if (!guestSiteLoadTelemDataLoading && guestSiteLoadTelemData && !hasEnrichedTelemetry) {
      enrichTelemetryExtraInfo({ ...extraInfo, guestSiteLoad: true });
      setHasEnrichedTelemetry(true);
    }
  }, [guestSiteLoadTelemDataLoading, guestSiteLoadTelemData, setHasEnrichedTelemetry, extraInfo, hasEnrichedTelemetry]);

  // Preview

  // Initialize preview data when query resolves
  useEffect(() => {
    if (isPreviewing) {
      setEventPreviewData(draft => {
        draft.eventPreview = data;
      });
    }
  }, [setEventPreviewData, data]);

  // Respond to event preview updates if previewing
  const handleOnPreviewUpdate = useEventCallback<OnPreviewListenerUpdate>(cb => {
    if (isPreviewing) {
      setEventPreviewData(draft => {
        const eventDraft = draft.eventPreview?.eventByName;
        if (eventDraft) {
          cb(eventDraft, setCurrentAdminPage);
        }
      });
    }
  });

  //Refetch Preview data
  const { updateEventPlaceCache } = useEventPlaceCacheUpdate();
  const [refetchPreviewLoading, setRefetchPreviewLoading] = useState(false);
  const handleOnRefetchData = useEventCallback(message => {
    if (message.updateEventPlaceCache) {
      updateEventPlaceCache(message.updateEventPlaceCache);
      return;
    }
    setRefetchPreviewLoading(true);
    const query = refetchPageQueries[message.value as GuestSitePage];
    if (message.value !== 'custom') {
      //Wait until the new world DB is triggered to update
      //Timeout should be removed when all Admin edit pages transferred to JoyWeb
      setTimeout(() => {
        const promises = [];

        promises.push(client.refetchQueries({ include: query ? [...query, 'BasicEventInfo'] : ['BasicEventInfo'] }));
        promises.push(refetch());

        Promise.all(promises)
          .catch(error => addError(error, { context: 'GuestSite', operation: 'refetchPreview' }))
          .finally(() => {
            setRefetchPreviewLoading(false);
          });
      }, 2000);
    } else {
      const promises = [];

      promises.push(client.refetchQueries({ include: query ? [...query, 'BasicEventInfo'] : ['BasicEventInfo'] }));
      promises.push(refetch());

      Promise.all(promises)
        .catch(error => addError(error, { context: 'GuestSite', operation: 'refetchPreview' }))
        .finally(() => {
          setRefetchPreviewLoading(false);
        });
    }
  });

  // Show/Hide BannerUnPublishedChanges
  useEffect(() => {
    if (!event || isPreviewing || view === 'draft') {
      return;
    }

    if (![EventUserStatus.admin, EventUserStatus.owner].includes(eventUserStatus)) {
      return;
    }

    if (hasUnpublishedChanges && !isWebsitePublishCapabilityEnabled) {
      setShowBannerReminderChanges(true);
      telemetry.notPublishedBanner('Shown');
    } else {
      setShowBannerReminderChanges(false);
    }
  }, [event, view, isAdmin, isSuperAdmin, eventUserStatus, hasUnpublishedChanges, telemetry, isWebsitePublishCapabilityEnabled]);

  const onDismissReminder = useCallback(() => {
    setShowBannerReminderChanges(false);
    telemetry.notPublishedBanner('Dismissed');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClickReminder = useCallback(() => {
    telemetry.notPublishedBanner('Published');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Annoucement

  useEffect(() => {
    withWindow(({ document }) => {
      const comesFromRegistry = !!document?.referrer?.endsWith('/registry');
      if (event?.info?.announcementBanner?.active && !isPreviewing && !comesFromRegistry && !isAnnouncementHidden) {
        setShowAnnouncement(true);
        telemetry.announcementBannerChange(event?.info?.announcementBanner?.title, 'Shown');
      } else {
        setShowAnnouncement(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);

  const onAnnouncementDismiss = useCallback((title: string) => {
    telemetry.announcementBannerChange(title, 'Dismissed');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePublishWebsite = useCallback(async () => {
    try {
      await websitePublishCapabilityFeature.eventPublishedState.update(true);

      if (event) {
        telemetry.websiteVisibilityPublish(event.id);
      }
      toast(tq`🥳 Your website has been successfully published!`);
    } catch (error) {
      addError(error, { context: 'GuestSiteController:handlePublishWebsite' });
      toast(tq`😬 Failed to publish website. Please try again.`);
    }
  }, [websitePublishCapabilityFeature.eventPublishedState, toast, tq, event, telemetry]);

  useReloadForLegacyGuestsite(event?.featureFlags, event?.website, event?.useLegacyGuestSite);
  const justFonts = useQueryParamHelper().getValue('justFonts');
  const rsvpPage = event?.pages?.find(page => page.pageSlug === 'rsvp');
  const disableActionBar = useProFeature(EventFeature.disableWebActionBar);
  const isBrannan = layout === 'brannan';
  const graphicAccent = isBrannan ? (event?.eventDesign?.accent ?? 'default') : undefined;
  const shouldRenderActionBar = (isBrannan || isLastPageVisible) && !isAdmin && !isPreviewing && !disableActionBar;

  const loadingGuestSite = loading || loadingFontPackData || loadingThemeData || (styledApplicatorLoading ? loadingTheme || loadingFonts : false);
  const newPages = useMemo(
    () => (isHideGetTheAppPageExperimentEnabled ? event?.pages : addAppPage(event?.website, event)),
    [addAppPage, event, isHideGetTheAppPageExperimentEnabled],
  );
  const customPages = usePagesWithPhotoAlignment(newPages, event?.pageContainers);
  const { pages: guestSitePages } = useGetGuestSitePages({ pages: customPages });
  const eventData = event
    ? {
        ...event,
        pages: guestSitePages as EventPageFragment[],
      }
    : undefined;

  //Filtering the hidden pages for guest, but display hidden page when it in Admin
  let eventWithVisiblePages = eventData;
  if (eventData && isPreviewing) {
    // Welcome page should always be visible in both website preview and designer preview, even if webiste is hidden
    const visiblePages = eventData.pages.filter(
      page =>
        (currentAdminPage === 'story' && page.type === 'tidbits') ||
        currentAdminPage === page.type ||
        !page.disabled ||
        page.type === 'welcome' ||
        (page.type === 'custom' && currentAdminPage === page.pageSlug) ||
        (page.type === 'custom' && !page.disabled && (page.pageSlug !== 'accommodations' || !hideAccomPreviewUntilFlagEnabled)),
    );
    eventWithVisiblePages = { ...eventData, pages: visiblePages };
  } else if (eventData) {
    eventWithVisiblePages = { ...eventData, pages: guestSitePages.length > 0 ? [...guestSitePages] : eventData.pages };
  }

  return {
    event: eventWithVisiblePages,
    layout,
    loading: loadingGuestSite || refetchPreviewLoading || guestSiteLoadTelemDataLoading,
    hasUnpublishedChanges,
    isAdmin,
    isPreviewing,
    handleOnPreviewUpdate,
    handleOnRefetchData,
    eventPageRoutes,
    theme,
    justFonts,
    rsvpPage,
    graphicAccent,
    shouldRenderActionBar,
    loadingTheme,
    showBannerReminderChanges,
    setLoadingTheme,
    setIsLastPageVisible,
    onDismissReminder,
    onClickReminder,
    showAnnouncement,
    onAnnouncementDismiss,
    currentAdminPage,
    isNativeAccommodationPage,
    isLoadingWebsitePublishCapability,
    showUnpublishedPage,
    showUnpublishedBanner,
    isPublishingWebsite: isWebsitePublishedUpdating,
    handlePublishWebsite,
    showAccommodationsVisibilityBanner,
    handleCloseAccommodationsVisibilityBanner,
    headerPresentationLayout: event?.eventDesign?.activeWebsiteHeaderPresentationLayout?.dataJSON as SupportedHeaderPhotoLayoutSchema | undefined,
  } as const;
};
